import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import WorkIcon from "@mui/icons-material/WorkOutlineOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import GroupIcon from "@mui/icons-material/GroupsOutlined";
import PeopleIcon from "@mui/icons-material/PersonOutlineOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";

// Navigation is data, not markup. The sidebar, the mobile bottom bar and the
// mobile "More" sheet all render from these lists.
//
// `primary: true` marks the items promoted into the mobile bottom tab bar.
// Keep at most four primary items per role — a fifth "More" tab is appended
// automatically when there are further items.
//
// `permission` is the key, or keys, that an item requires; holding any one of
// them is enough, matching the server. An item with no `permission` is open
// to anyone in that shell, which is how the personal screens — your own
// dashboard, your own timesheet — stay reachable whatever the role.
//
// There is one list per shell rather than one per role: roles are created by
// admins now, so a role-keyed list could not be written down here.

export const adminNav = [
  { label: "Dashboard", to: "/admin", icon: DashboardIcon, primary: true, end: true, section: "Overview" },
  // The caller's own week. Admins log hours too, and a role's portal decides
  // only which shell it lands in — so the personal timesheet has to exist in
  // both, or moving someone into an admin-portal role takes it away from them.
  { label: "My Timesheet", to: "/admin/time/add", icon: CalendarMonthIcon, section: "Overview", permission: "timesheets.create" },

  { label: "Projects", to: "/admin/projects", icon: WorkIcon, primary: true, section: "Workspace", permission: "projects.view" },
  { label: "Invoices", to: "/admin/invoices", icon: ReceiptLongIcon, primary: true, section: "Workspace", permission: "invoices.view" },
  { label: "Expenses", to: "/admin/expenses", icon: PaymentsIcon, section: "Workspace" },
  { label: "Clients", to: "/admin/clients", icon: BusinessIcon, section: "Workspace", permission: "clients.view" },

  { label: "Groups", to: "/admin/groups", icon: GroupIcon, section: "People", permission: "groups.view" },
  { label: "Users", to: "/admin/users", icon: PeopleIcon, section: "People", permission: "users.view" },
  { label: "Roles & Permissions", to: "/admin/roles", icon: ShieldIcon, section: "People", permission: "roles.manage" },

  // Reads the invoice list *and* the project list — a project that was never
  // invoiced is the whole point of the page, and it can only come from the
  // project list. Both, or the link leads to a half-empty table.
  { label: "Invoice Summary", to: "/admin/invoices/summary", icon: ReceiptLongIcon, section: "Reports", permission: ["invoices.view", "projects.view"], requireAll: true },
  { label: "Expense Report", to: "/admin/expenses/report", icon: AssessmentIcon, section: "Reports", permission: "reports.expense" },
  { label: "Time Report", to: "/admin/time-report", icon: AccessTimeIcon, section: "Reports", permission: "reports.time" },
  { label: "Weekly Time Report", to: "/admin/weekly-time-report", icon: CalendarMonthIcon, section: "Reports", permission: "timesheets.view_all" },
];

export const userNav = [
  { label: "Dashboard", to: "/user", icon: DashboardIcon, primary: true, end: true, section: "Overview" },
  { label: "Manage Time", to: "/user/phases", icon: WorkIcon, primary: true, section: "Time", permission: "projects.view" },
  { label: "Timesheet", to: "/user/time/add", icon: CalendarMonthIcon, primary: true, section: "Time", permission: "timesheets.create" },
  { label: "Expenses", to: "/user/expenses", icon: PaymentsIcon, primary: true, section: "Workspace" },
  { label: "Expense Report", to: "/user/expenses/report", icon: AssessmentIcon, section: "Reports", permission: "reports.expense" },
  // Reading the team's hours, not just your own. This is what the Manager
  // role was created for, but any role granted reports.time now gets it.
  { label: "Time Report", to: "/user/time-report", icon: AccessTimeIcon, section: "Reports", permission: "reports.time" },
];

/**
 * The nav for a session: its shell's list, minus whatever it may not reach.
 *
 * The two predicates are passed in rather than imported so this file stays
 * free of storage access and can be unit-tested with plain functions.
 */
export const getNavItems = (portal, has = () => true, hasAll = () => true) => {
  const items = portal === "admin" ? adminNav : userNav;
  return items.filter((item) => {
    if (!item.permission) return true;
    const keys = Array.isArray(item.permission) ? item.permission : [item.permission];
    return item.requireAll ? hasAll(keys) : has(keys);
  });
};

// Nine flat entries read as one undifferentiated list. Group them so the
// sidebar has a scannable shape; order within the config is preserved.
export const groupNav = (items) => {
  const groups = [];
  items.forEach((item) => {
    const name = item.section || "";
    const last = groups[groups.length - 1];
    if (last && last.section === name) last.items.push(item);
    else groups.push({ section: name, items: [item] });
  });
  return groups;
};

// Items shown as bottom tabs, and the remainder that go into the "More" sheet.
export const splitNav = (items) => {
  const primary = items.filter((i) => i.primary).slice(0, 4);
  const rest = items.filter((i) => !primary.includes(i));
  return { primary, rest };
};

// Most screens don't render a PageHeader, so the mobile top bar has no title to
// show on a detail route like /admin/view-time-sheet/123 — it fell back to the
// app name. Humanise the route instead: "View Time Sheet".
export const titleFromPath = (pathname) => {
  const parts = pathname
    .split("/")
    .filter(Boolean)
    .filter((part) => part !== "admin" && part !== "user")
    // Drop record ids so "/clients/12" doesn't title itself "12".
    .filter((part) => !/^\d+$/.test(part));

  const last = parts[parts.length - 1];
  if (!last) return null;

  return last
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Longest matching path wins, so /admin/projects doesn't also light up /admin.
export const findActiveItem = (items, pathname) => {
  let best = null;
  items.forEach((item) => {
    const matches = item.end
      ? pathname === item.to
      : pathname === item.to || pathname.startsWith(item.to + "/");
    if (matches && (!best || item.to.length > best.to.length)) best = item;
  });
  return best;
};
